import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminSectionCard, {
  AdminButton,
  AdminEmptyState,
  AdminScrollTable,
  AdminStatCard,
  AdminStatusBadge,
} from "./AdminSectionCard";
import api from "../../api/axios";
import {
  useGetAdminContestQuery,
  useGetAdminContestRegistrationsQuery,
} from "../../features/api/appApi";
import {
  STATUS_OPTIONS,
  statusTone,
  sortedParticipants,
  errMsg,
  AddRegistrationModal,
  ManageRegistrationModal,
} from "./AdminContestRegistrationShared";

export default function AdminContestRegistrationsPage() {
  const { contestId } = useParams();

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [status, setStatus] = useState("");
  const [reference, setReference] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [page, setPage] = useState(1);
  const limit = 20;

  const [selectedReg, setSelectedReg] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const {
    data: contest,
    isLoading: contestLoading,
    error: contestError,
    refetch: refetchContest,
  } = useGetAdminContestQuery(contestId);

  const {
    data,
    isLoading,
    isFetching,
    error: listError,
    refetch: refetchRegistrations,
  } = useGetAdminContestRegistrationsQuery({
    contestId,
    search,
    status,
    reference,
    sortBy,
    sortOrder,
    page,
    limit,
  });

  const { data: fullData, refetch: refetchFullData } = useGetAdminContestRegistrationsQuery({
    contestId,
    limit: 2000,
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    setError("");
    try {
      await Promise.all([refetchContest(), refetchRegistrations(), refetchFullData()]);
      setSuccess("Data table refreshed.");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setRefreshing(false);
    }
  };

  const registrations = data?.registrations || [];
  const stats = data?.stats || { total: 0, verified: 0, pending: 0, rejected: 0 };
  const totalPages = data?.totalPages || 1;
  const total = data?.total || 0;
  const referenceBreakdown = data?.referenceBreakdown || [];

  useEffect(() => {
    if (listError) setError(errMsg(listError));
  }, [listError]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(""), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleCopy = async (kind) => {
    try {
      const allRegs = fullData?.registrations || [];
      let list = [];
      if (kind === "all-emails") {
        list = allRegs.flatMap((r) => sortedParticipants(r).map((p) => p.email));
      } else if (kind === "all-phones") {
        list = allRegs.flatMap((r) => sortedParticipants(r).map((p) => p.phone));
      } else if (kind === "leader-emails") {
        list = allRegs.flatMap((r) => sortedParticipants(r).filter((p) => p.role === "leader").map((p) => p.email));
      } else if (kind === "leader-phones") {
        list = allRegs.flatMap((r) => sortedParticipants(r).filter((p) => p.role === "leader").map((p) => p.phone));
      }
      const cleaned = [...new Set(list.map((v) => String(v || "").trim()).filter(Boolean))];
      await navigator.clipboard.writeText(cleaned.join(", "));
      setSuccess(`${cleaned.length} item(s) copied to clipboard!`);
    } catch (err) {
      setError("Failed to copy data: " + (err?.message || "unknown error"));
    }
  };

  const handleDownloadExcel = async () => {
    setExporting(true);
    setError("");
    try {
      const response = await api.get(`/admin/contests/${contestId}/export`, {
        params: { search, status, reference, sortBy, sortOrder },
        responseType: "blob",
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const namePart = contest?.slug || contestId;
      a.download = `${namePart}-registrations-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to download spreadsheet.");
    } finally {
      setExporting(false);
    }
  };

  if (contestLoading) {
    return <div className="py-12 text-center text-sm font-semibold text-slate-500">Loading contest...</div>;
  }

  if (contestError || !contest) {
    return (
      <div className="space-y-4">
        <Link to="/admin/contests" className="text-sm font-semibold text-blue-600 hover:text-blue-700">
          ← Back to Contests
        </Link>
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-800">
          {errMsg(contestError) || "Contest not found."}
        </div>
      </div>
    );
  }

  const isTeam = contest.type === "team";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/admin/contests"
            className="mb-2 inline-block text-sm font-semibold text-blue-600 hover:text-blue-700"
          >
            ← Back to Contests
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-black tracking-tight text-slate-900">{contest.title}</h1>
            <AdminStatusBadge tone="blue">
              {isTeam
                ? `Team (${contest.minTeamSize || 1}–${contest.maxTeamSize || 1} members)`
                : "Individual"}
            </AdminStatusBadge>
            <AdminStatusBadge tone={contest.editingEnabled ? "green" : "slate"}>
              Self-edit: {contest.editingEnabled ? "Unlocked" : "Locked"}
            </AdminStatusBadge>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/admin/contests/${contestId}/participants`}>
            <AdminButton variant="secondary">View Participants</AdminButton>
          </Link>
          <AdminButton onClick={() => setShowAddModal(true)} variant="success">
            + Add Registration
          </AdminButton>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <AdminStatCard title="Total" value={stats.total} tone="blue" />
        <AdminStatCard title="Verified" value={stats.verified} tone="green" />
        <AdminStatCard title="Pending" value={stats.pending} tone="amber" />
        <AdminStatCard title="Rejected" value={stats.rejected} tone="slate" />
      </div>

      {success ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-800">
          {success}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-800">
          {error}
        </div>
      ) : null}

      <AdminSectionCard title="Quick Copy Options" subtitle="Copy participant contact lists to clipboard">
        <div className="flex flex-wrap gap-3">
          <AdminButton onClick={() => handleCopy("all-emails")}>Copy All Emails</AdminButton>
          <AdminButton onClick={() => handleCopy("all-phones")}>Copy All Phone Numbers</AdminButton>
          {isTeam ? (
            <>
              <AdminButton onClick={() => handleCopy("leader-emails")} variant="secondary">
                Copy Leader Emails
              </AdminButton>
              <AdminButton onClick={() => handleCopy("leader-phones")} variant="secondary">
                Copy Leader Phones
              </AdminButton>
            </>
          ) : null}
        </div>
      </AdminSectionCard>

      {referenceBreakdown.length > 0 ? (
        <AdminSectionCard
          title="Reference Report"
          subtitle="How many teams came in through each reference. Click a row to filter the table below."
        >
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setReference("");
                setPage(1);
              }}
              className={`rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${
                reference === ""
                  ? "border-slate-800 bg-slate-800 text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              All ({stats.total})
            </button>
            {referenceBreakdown.map((row) => {
              const value = row.reference === null ? "__none__" : row.reference;
              const label = row.reference === null ? "No reference" : row.reference;
              const isActive = reference === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    setReference(value);
                    setPage(1);
                  }}
                  className={`rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${
                    isActive
                      ? "border-slate-800 bg-slate-800 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {label} ({row.count})
                </button>
              );
            })}
          </div>
        </AdminSectionCard>
      ) : null}

      <AdminSectionCard
        title="Registrations"
        subtitle="Manage verification statuses and view registration details"
        rightAction={
          <AdminButton
            type="button"
            variant="secondary"
            onClick={handleRefresh}
            disabled={refreshing || isLoading}
            className="h-9 px-3 text-xs"
          >
            <span className={refreshing ? "inline-block animate-spin" : "inline-block"}>⟳</span>
            <span className="ml-1.5">{refreshing ? "Refreshing..." : "Refresh"}</span>
          </AdminButton>
        }
      >
        <form onSubmit={handleSearchSubmit} className="mb-5 flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            placeholder="Search by name, email, phone, ID, team name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
          />
          <div className="flex flex-wrap gap-2">
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-400"
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              value={reference}
              onChange={(e) => {
                setReference(e.target.value);
                setPage(1);
              }}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-400"
            >
              <option value="">All References</option>
              {referenceBreakdown.map((row) => {
                const value = row.reference === null ? "__none__" : row.reference;
                const label = row.reference === null ? "No reference" : row.reference;
                return (
                  <option key={value} value={value}>
                    {label} ({row.count})
                  </option>
                );
              })}
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-400"
            >
              <option value="createdAt">Date Submitted</option>
              {isTeam ? <option value="teamName">Team Name</option> : null}
              <option value="status">Status</option>
            </select>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-400"
            >
              <option value="DESC">Descending</option>
              <option value="ASC">Ascending</option>
            </select>
            <AdminButton type="submit">Search</AdminButton>
            <AdminButton type="button" onClick={handleDownloadExcel} variant="secondary" disabled={exporting}>
              {exporting ? "Exporting..." : "Download Sheet"}
            </AdminButton>
          </div>
        </form>

        {isLoading || isFetching ? (
          <div className="py-12 text-center text-sm font-semibold text-slate-500">Loading registrations...</div>
        ) : registrations.length > 0 ? (
          <div className="space-y-4">
            <AdminScrollTable>
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Code</th>
                    {isTeam ? <th className="px-4 py-3">Team Name</th> : null}
                    <th className="px-4 py-3">Reference</th>
                    <th className="px-4 py-3">Participants</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {registrations.map((row) => (
                    <tr
                      key={row.id}
                      className="cursor-pointer bg-white hover:bg-slate-50/70"
                      onClick={() => setSelectedReg(row)}
                    >
                      <td className="px-4 py-4 font-mono text-xs text-slate-600">{row.registrationCode}</td>
                      {isTeam ? (
                        <td className="px-4 py-4 font-semibold text-slate-800">{row.teamName}</td>
                      ) : null}
                      <td className="px-4 py-4 text-xs text-slate-600">
                        {row.reference || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-4">
                        <div className="max-w-xs text-xs text-slate-600">
                          {sortedParticipants(row)
                            .map(
                              (p) =>
                                `${p.name} (${p.role === "leader" ? "Leader" : p.role === "individual" ? "Entrant" : "Member"})`
                            )
                            .join(", ")}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <AdminStatusBadge tone={statusTone(row.status)}>{row.status}</AdminStatusBadge>
                      </td>
                      <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-2">
                          <AdminButton
                            onClick={() => setSelectedReg(row)}
                            className="h-9 px-3 text-xs"
                          >
                            Manage
                          </AdminButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminScrollTable>

            <div className="flex items-center justify-between border-t border-slate-100 pt-4">
              <span className="text-xs text-slate-500">
                Showing page {page} of {totalPages} ({total} total registrations)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        ) : (
          <AdminEmptyState text="No registrations match the query." />
        )}
      </AdminSectionCard>

      {showAddModal ? (
        <AddRegistrationModal
          contest={contest}
          onClose={() => setShowAddModal(false)}
          onCreated={(msg) => {
            setShowAddModal(false);
            setSuccess(msg);
          }}
        />
      ) : null}

      {selectedReg ? (
        <ManageRegistrationModal
          contest={contest}
          registration={selectedReg}
          onClose={() => setSelectedReg(null)}
          onUpdated={(msg) => {
            setSelectedReg(null);
            setSuccess(msg);
          }}
          onDeleted={(msg) => {
            setSelectedReg(null);
            setSuccess(msg);
          }}
        />
      ) : null}
    </div>
  );
}
